import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  parseSessionPath,
  reduceEvents,
  subscribeToEvents,
  formatElapsed,
} from '../lib/raceEvents';

function StatusBadge({ status }) {
  const labels = {
    waiting: 'Waiting for start',
    racing: 'Racing',
    finished: 'Finished',
    abandoned: 'Abandoned',
  };
  return (
    <span className={`race-status race-status--${status}`} data-testid="status-badge">
      {labels[status] ?? status}
    </span>
  );
}

function LegTimesTable({ legTimes }) {
  if (!legTimes.length) return null;
  return (
    <table className="leg-times-table" data-testid="leg-times-table">
      <thead>
        <tr>
          <th>Leg</th>
          <th>Elapsed</th>
        </tr>
      </thead>
      <tbody>
        {legTimes.map((leg, i) => (
          <tr key={i}>
            <td>Mark {leg.fromMark} → {leg.toMark}</td>
            <td>{formatElapsed(leg.elapsed)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FinishBanner({ finishTime, raceStartTime, finishSource }) {
  const elapsed = finishTime && raceStartTime ? finishTime - raceStartTime : null;
  return (
    <div className="finish-banner" data-testid="finish-banner">
      <h2>Race Finished</h2>
      {elapsed != null && <p>Finish time: {formatElapsed(elapsed)}</p>}
      {finishSource && <p>Source: {finishSource}</p>}
    </div>
  );
}

function LatestPosition({ position, count }) {
  return (
    <section className="latest-position" data-testid="latest-position">
      <h2>Latest position</h2>
      <p>Positions received: {count}</p>
      {position ? (
        <>
          <p>Time: {position.timestamp ?? '—'} <span>(device time)</span></p>
          <p>
            Latitude: {Number.isFinite(position.latitude) ? position.latitude.toFixed(4) : '—'}
            {' · '}
            Longitude: {Number.isFinite(position.longitude) ? position.longitude.toFixed(4) : '—'}
          </p>
          <p>
            Speed: {Number.isFinite(position.speed) ? `${(position.speed * 1.943844).toFixed(1)} kn` : '—'}
          </p>
        </>
      ) : (
        <p>No positions received yet.</p>
      )}
    </section>
  );
}

export default function RaceLive() {
  const [searchParams] = useSearchParams();
  const sessionParam = searchParams.get('s');
  const parsed = useMemo(() => parseSessionPath(sessionParam), [sessionParam]);

  const [meta, setMeta] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  // Subscribe to _meta document to find leader device path
  useEffect(() => {
    if (!parsed) return;
    const metaRef = doc(db, parsed.sessionPath, '_meta');
    const unsub = onSnapshot(metaRef, (snap) => {
      if (snap.exists()) setMeta(snap.data());
    }, (err) => setError(err.message));
    return unsub;
  }, [parsed]);

  // Subscribe to leader's events once we know the device path
  useEffect(() => {
    if (!meta?.leaderDevicePath) return;
    const eventsPath = meta.leaderDevicePath;
    const unsub = subscribeToEvents(eventsPath, setEvents, (err) => setError(err.message));
    return unsub;
  }, [meta?.leaderDevicePath]);

  const raceState = useMemo(() => reduceEvents(events), [events]);

  if (!sessionParam || !parsed) {
    return (
      <div className="page" data-testid="race-live-error">
        <h1>Live Race Viewer</h1>
        <p>Missing or invalid session parameter. Provide <code>?s=encoded-session-path</code> in the URL.</p>
        <Link to="/">Back to home</Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page" data-testid="race-live-error">
        <h1>Live Race Viewer</h1>
        <p>Error: {error}</p>
        <Link to="/">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="page" data-testid="race-live">
      <h1>Live Race Viewer</h1>

      <div className="race-info">
        <p><strong>Club:</strong> {parsed.club}</p>
        <p><strong>Fleet:</strong> {parsed.fleetClass}</p>
        <p><strong>Vessel:</strong> {parsed.vesselId}</p>
        <p><strong>Date:</strong> {parsed.date}</p>
        {raceState.courseName && <p><strong>Course:</strong> {raceState.courseName}</p>}
      </div>

      <StatusBadge status={raceState.status} />

      {raceState.status === 'waiting' && (
        <p data-testid="waiting-message">Waiting for race to start...</p>
      )}

      {raceState.status === 'racing' && (
        <p data-testid="current-mark">Current mark: {raceState.currentMarkIndex}</p>
      )}

      <LegTimesTable legTimes={raceState.legTimes} />

      <LatestPosition position={raceState.latestPosition} count={raceState.positionCount} />

      {raceState.status === 'finished' && (
        <FinishBanner
          finishTime={raceState.finishTime}
          raceStartTime={raceState.raceStartTime}
          finishSource={raceState.finishSource}
        />
      )}

      {raceState.status === 'abandoned' && (
        <div className="abandon-banner" data-testid="abandon-banner">
          <h2>Race Abandoned</h2>
        </div>
      )}

      <p className="event-count">Events received: {raceState.eventCount}</p>
    </div>
  );
}
